import type { MediaResponse } from "@/api/media.api";
import { TableHeader } from "@/components/tables/add-header";
import type { DropdownItem } from "@/components/tables/data-row";
import { DataTable } from "@/components/tables/data-table";
import { ConfirmModal } from "@/components/ui/confirm-modal";
import { useTRPC } from "@/lib/trpc";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";

const columns: ColumnDef<MediaResponse>[] = [
  {
    accessorKey: "title",
    header: "Title",
  },
  {
    accessorKey: "description",
    header: "Description",
  },
  {
    accessorKey: "cloudStorageObject.fullUrl",
    header: "File",
    cell: ({ row }) => {
      const url = row.original.cloudStorageObject?.fullUrl;
      return url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline text-sm"
        >
          View File
        </a>
      ) : (
        "-"
      );
    },
  },
];

function MediaRoute() {
  const [deleteMediaId, setDeleteMediaId] = useState<string | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const trpc = useTRPC();

  const mediaQuery = useSuspenseQuery(trpc.media.list.queryOptions());
  const media = mediaQuery.data as MediaResponse[];

  const { mutate: deleteMediaMutation, isPending: isLoading } = useMutation(
    trpc.media.delete.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries(trpc.media.list.queryOptions());
        setDeleteMediaId(null);
      },
    }),
  );

  const dropdownItems: DropdownItem<MediaResponse>[] = [
    {
      label: "Edit",
      onClick: ({ id }) => {
        navigate({
          to: `/media/${id}`,
        });
      },
    },
    {
      label: "Delete",
      onClick: ({ id }) => {
        setDeleteMediaId(id);
      },
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <TableHeader linkTo="/media/new" buttonText="Upload Media" />
      </div>
      <DataTable
        data={media}
        columns={columns}
        title="Media Library"
        description="Manage your uploaded media files"
        dropdownItems={dropdownItems}
      />
      <ConfirmModal
        open={Boolean(deleteMediaId)}
        onConfirm={() => {
          if (deleteMediaId) deleteMediaMutation({ id: deleteMediaId });
        }}
        onDismiss={() => {
          setDeleteMediaId(null);
        }}
        isLoading={isLoading}
      />
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/media/")({
  component: () => <MediaRoute />,
});
